import { alpha, Box, Chip, Stack, Tooltip } from '@mui/material';
import IconHandler from 'components/common/icon/IconHandler';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SearchTopics } from 'api/search';
import IconMi from 'components/common/icon/IconMi';
import styles from './TopicsBlock.styles';
import useBundleTranslation from 'i18n';
import { RenderableComponent } from 'tools/types';
import { useGlossaryTermVisibility } from 'hooks/useGlossaryTerm';
import { IGlossarySettings } from 'store/auth';
import { useAppSelector } from 'store/hooks';
import { openInNewTab } from 'tools/tools';
import { useSelector } from 'react-redux';

type IProps = {
    topics: SearchTopics;
    elementId: number | string;
    handleTagClick?: (
        isTagsOptions: boolean,
        topic: { topic_id: number | null; name: string },
        elementId: number | string,
    ) => void;
};

const TopicsBlock: RenderableComponent<IProps> = ({ topics, handleTagClick, elementId }: IProps) => {
    const { t } = useBundleTranslation(['components/common/element']);
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});

    const parentRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const [isToggleBehavior, setIsToggleBehavior] = useState<{ [key: string]: boolean }>({});

    const { isVisibleGlossaryTermByStatus } = useGlossaryTermVisibility();

    const glossarySettings: IGlossarySettings | undefined = useAppSelector(
        (state) => state.auth?.configuration?.glossarySettings,
    );
    const hostname = useSelector((state: any) => state.auth?.userInfo?.hostname) ?? '';

    useEffect(() => {
        const updateVisibleChips = () => {
            const newToggleBehavior: { [key: string]: boolean } = {};
            const itemsIndent = 16;
            const indentBetweenTag = 2;

            Object.keys(parentRefs.current).forEach((sectionKey) => {
                const parent = parentRefs.current[sectionKey];
                if (!parent) return false;

                const elementsContainer = parent.querySelector('.term-section__items');
                if (!elementsContainer) return false;

                const availableWidth = parent.offsetWidth - itemsIndent + indentBetweenTag; // Adjust for padding/margin

                let totalTagsWidth = 0;
                Array.from(elementsContainer.children).forEach((tag: any) => {
                    totalTagsWidth += tag.offsetWidth + indentBetweenTag;
                });
                newToggleBehavior[sectionKey] = totalTagsWidth > availableWidth;
            });

            setIsToggleBehavior(newToggleBehavior);
        };

        updateVisibleChips();
        window.addEventListener('resize', updateVisibleChips);

        return () => {
            window.removeEventListener('resize', updateVisibleChips);
        };
    }, []);

    if (!(topics && Object.keys(topics).length > 0)) return null;

    const termsSectionKeys = useMemo(() => Object.keys(topics), [topics])
        .sort((a, b) => {
            if (a === 'default-topic-type') return -1;
            if (b === 'default-topic-type') return 1;

            const termA = topics[a].display_order;
            const termB = topics[b].display_order;

            return termA - termB;
        })
        .filter((key: any) => {
            if (key == 'default-topic-type') return true;
            const sectionTopics = topics[key]?.topics ?? [];
            return sectionTopics.some((topic: any) => {
                return isVisibleGlossaryTermByStatus(topic);
            });
        });

    const toggleExpand = (categoryKey: string) => {
        setExpanded((prev) => ({ ...prev, [categoryKey]: !prev[categoryKey] }));
    };

    return (
        <Box sx={{ mt: 1.5 }} className={'topics-block'}>
            {termsSectionKeys.length > 0 && (
                <>
                    {termsSectionKeys.map((sectionKey, i) => {
                        const section = topics[sectionKey];
                        return (
                            <Stack key={i} sx={styles.termSection}>
                                <Stack sx={styles.termSectionHeader}>
                                    <Box sx={styles.iconHolder}>
                                        {section.use_icon ? (
                                            <IconHandler
                                                sx={{ fontSize: '14px' }}
                                                icon={{
                                                    type: section.use_icon == 'uploaded' ? 'upload' : section.use_icon,
                                                    value:
                                                        section.use_icon == 'uploaded'
                                                            ? `data:${section.icon};base64, ${section.icon}`
                                                            : section.fa_icon,
                                                }}
                                            />
                                        ) : (
                                            <IconHandler
                                                sx={{ fontSize: '16px' }}
                                                icon={{ type: 'mi', value: 'tags' }}
                                            />
                                        )}
                                    </Box>

                                    <Box>
                                        {section.topic_type_name}{' '}
                                        <Box component={'span'} fontWeight="400">
                                            ({section.topics.length})
                                        </Box>
                                    </Box>
                                </Stack>
                                <Box
                                    display="flex"
                                    width="100%"
                                    sx={{
                                        overflow: 'hidden',
                                        height:
                                            isToggleBehavior[sectionKey] && !expanded[sectionKey] ? '20px' : undefined,
                                    }}
                                    ref={(el) => (parentRefs.current[sectionKey] = el as any)}
                                >
                                    <Stack
                                        direction="row"
                                        flexWrap={'wrap'}
                                        flexGrow={1}
                                        className={'term-section__items'}
                                        sx={styles.termSectionItems}
                                    >
                                        {section.topics
                                            .filter((topic: any) => isVisibleGlossaryTermByStatus(topic))
                                            .map((topic: any, index: number) => {
                                                const needToCutLabel = topic.name.length > 30;
                                                const label = needToCutLabel
                                                    ? topic.name.substring(0, 30) + '...'
                                                    : topic.name;

                                                return (
                                                    <Box key={`${label}_${index}`}>
                                                        <Tooltip title={needToCutLabel ? topic.name : ''}>
                                                            <Chip
                                                                onClick={() => {
                                                                    const isTagsOptions =
                                                                        sectionKey == 'default-topic-type';
                                                                    if (
                                                                        !isTagsOptions &&
                                                                        glossarySettings?.link_to_custom &&
                                                                        glossarySettings?.custom_url_pattern > ''
                                                                    ) {
                                                                        openInNewTab(
                                                                            glossarySettings.custom_url_pattern
                                                                                .replaceAll('[hostname]', hostname)
                                                                                .replaceAll(
                                                                                    '[term id]',
                                                                                    topic.topic_id,
                                                                                ),
                                                                        );
                                                                    } else if (handleTagClick) {
                                                                        handleTagClick(isTagsOptions, topic, elementId);
                                                                    }
                                                                }}
                                                                className={'default'}
                                                                label={label}
                                                                color={'primary'}
                                                                sx={styles.topicItem}
                                                            />
                                                        </Tooltip>
                                                    </Box>
                                                );
                                            })}
                                    </Stack>
                                    {isToggleBehavior[sectionKey] && (
                                        <Box
                                            sx={styles.topicExpandCollapseButton}
                                            onClick={() => {
                                                toggleExpand(sectionKey);
                                            }}
                                        >
                                            <Box>
                                                {t(
                                                    !expanded[sectionKey]
                                                        ? 'description_show_more'
                                                        : 'description_show_less',
                                                )}
                                            </Box>
                                            <IconMi
                                                icon={!expanded[sectionKey] ? 'chevron-down' : 'chevron-up'}
                                                sx={{ fontSize: 16 }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </Stack>
                        );
                    })}
                </>
            )}
        </Box>
    );
};

TopicsBlock.shouldRender = ({ topics }: IProps): boolean => {
    return !!topics && Object.keys(topics).length > 0;
};

export default TopicsBlock;
